import React, { useRef, useState, useEffect } from 'react';

/**
 * AnimateReveal - Scroll-triggered reveal animations
 * @param {string} animation - 'fadeUp' | 'fadeIn' | 'slideLeft' | 'slideRight'
 * @param {number} delay - Stagger delay in ms
 * @param {string} className - Additional CSS classes
 * @param {boolean} once - Only animate once (default true)
 * @param {number} threshold - Intersection threshold 0-1 (default 0.1)
 */
const AnimateReveal = ({ 
  children, 
  animation = 'fadeUp', 
  delay = 0, 
  className = '', 
  once = true,
  threshold = 0.1 
}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin: '0px 0px -30px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold]);

  return (
    <div
      ref={ref}
      className={`animate-reveal animate-reveal--${animation} ${isVisible ? 'animate-reveal--visible' : ''} ${className}`}
      style={{ transitionDelay: isVisible ? `${delay}ms` : '0ms' }}
    >
      {children}
    </div>
  );
};

export default AnimateReveal;
